import { useEffect } from "react";
import { Form } from "semantic-ui-react";
import { PermitFieldValueInput, StateKey } from "../models/globalTypes";
import {
  BooleanTemplateField,
  DateTemplateField,
  NumberTemplateField,
  TemplateField,
  TemplateFieldType,
  TextTemplateField,
} from "../models/TemplateField";
import { FormError } from "../models/FormError";
import TextFieldInput from "./fields/TextFieldInput";
import BooleanFieldInput from "./fields/BooleanFieldInput";
import NumberFieldInput from "./fields/NumberFieldInput";
import DateFieldInput from "./fields/DateFieldInput";
import { StateKeyList } from "../models/StateKey";

interface Props {
  templateFields: TemplateField[];
  fieldValues: PermitFieldValueInput[];
  onChange: (fieldValues: PermitFieldValueInput[]) => void;
  /** The current stage key — fields are editable only if this stage is in their editableAtStages */
  editableStageKey: StateKey;
  /** The state key for which fields are required */
  requiredForStateKey: StateKey;
  /** Validation errors keyed by field key */
  fieldErrors: FormError;
}

const PermitFieldValuesForm = ({
  templateFields,
  fieldValues,
  onChange,
  editableStageKey,
  requiredForStateKey,
  fieldErrors,
}: Props) => {
  // Initialize default values for boolean fields that have a defaultValue but no entry in fieldValues yet.
  // This must happen here (not in child components) so all defaults are applied in a single onChange call,
  // avoiding stale-closure race conditions when multiple fields try to update independently.
  useEffect(() => {
    if (!templateFields || templateFields.length === 0) return;

    const defaults: PermitFieldValueInput[] = [];
    for (const field of templateFields) {
      if (field.fieldType === TemplateFieldType.Boolean) {
        const boolField = field as BooleanTemplateField;
        const existing = fieldValues.find((fv) => fv.fieldKey === field.key);
        if (
          (!existing || existing.booleanValue === undefined || existing.booleanValue === null) &&
          boolField.defaultValue !== null &&
          boolField.defaultValue !== undefined
        ) {
          defaults.push({ fieldKey: field.key, booleanValue: boolField.defaultValue });
        }
      }
    }

    if (defaults.length > 0) {
      const updated = [...fieldValues];
      for (const def of defaults) {
        const idx = updated.findIndex((fv) => fv.fieldKey === def.fieldKey);
        if (idx >= 0) {
          updated[idx] = { ...updated[idx], ...def };
        } else {
          updated.push(def);
        }
      }
      onChange(updated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateFields]);

  if (!templateFields || templateFields.length === 0) {
    return null;
  }

  const sortedFields = [...templateFields].sort((a, b) => a.displayOrder - b.displayOrder);

  const getFieldValue = (fieldKey: string): PermitFieldValueInput | undefined => {
    return fieldValues.find((fv) => fv.fieldKey === fieldKey);
  };

  const updateFieldValue = (fieldKey: string, update: Partial<PermitFieldValueInput>) => {
    const existing = fieldValues.find((fv) => fv.fieldKey === fieldKey);
    if (existing) {
      onChange(fieldValues.map((fv) => (fv.fieldKey === fieldKey ? { ...fv, ...update } : fv)));
    } else {
      onChange([...fieldValues, { fieldKey, ...update }]);
    }
  };

  const isFieldEditable = (field: TemplateField): boolean => {
    return field.editableAtStages?.includes(editableStageKey) ?? false;
  };

  const renderField = (field: TemplateField) => {
    const disabled = !isFieldEditable(field);
    const error = fieldErrors[field.key];
    const required = isRequiredAtState(field, requiredForStateKey);

    switch (field.fieldType) {
      case TemplateFieldType.Text:
        return (
          <TextFieldInput
            field={field as TextTemplateField}
            value={getFieldValue(field.key)}
            onChange={updateFieldValue}
            disabled={disabled}
            error={error}
            required={required}
          />
        );
      case TemplateFieldType.Boolean:
        return (
          <BooleanFieldInput
            field={field as BooleanTemplateField}
            value={getFieldValue(field.key)}
            onChange={updateFieldValue}
            disabled={disabled}
            error={error}
            required={required}
          />
        );
      case TemplateFieldType.Number:
        return (
          <NumberFieldInput
            field={field as NumberTemplateField}
            value={getFieldValue(field.key)}
            onChange={updateFieldValue}
            disabled={disabled}
            error={error}
            required={required}
          />
        );
      case TemplateFieldType.Date:
        return (
          <DateFieldInput
            field={field as DateTemplateField}
            value={getFieldValue(field.key)}
            onChange={updateFieldValue}
            disabled={disabled}
            error={error}
            required={required}
          />
        );
      default:
        return null;
    }
  };

  // Render fields in pairs (two-column layout)
  const fieldPairs: TemplateField[][] = [];
  for (let i = 0; i < sortedFields.length; i += 2) {
    fieldPairs.push(sortedFields.slice(i, i + 2));
  }

  return (
    <>
      {fieldPairs.map((pair, idx) => (
        <Form.Group widths="2" key={idx}>
          {pair.map((field) => renderField(field))}
        </Form.Group>
      ))}
    </>
  );
};

export default PermitFieldValuesForm;

/**
 * State ordering that mirrors the backend StateKey enum.
 * Used for cumulative required-field checks: a field required at an earlier
 * state remains required at all later states.
 */
const stateOrder: Record<string, number> = {
  [StateKeyList.REQUEST_IN_PROGRESS.key]: 0,
  [StateKeyList.REQUESTED.key]: 1,
  [StateKeyList.REJECTED.key]: 2,
  [StateKeyList.IN_PROGRESS.key]: 3,
  [StateKeyList.CANCELLED.key]: 4,
  [StateKeyList.READY_TO_ISSUE.key]: 5,
  [StateKeyList.ISSUED.key]: 6,
  [StateKeyList.EXPIRED.key]: 7,
  [StateKeyList.COMPLETED.key]: 8,
};

function isFieldEmpty(field: TemplateField, fv: PermitFieldValueInput | undefined): boolean {
  switch (field.fieldType) {
    case TemplateFieldType.Text:
      return !fv?.stringValue;
    case TemplateFieldType.Boolean:
      return fv?.booleanValue === null || fv?.booleanValue === undefined;
    case TemplateFieldType.Number:
      return fv?.numberValue === null || fv?.numberValue === undefined;
    case TemplateFieldType.Date:
      return !fv?.dateValue;
    default:
      return true;
  }
}

function isRequiredAtState(field: TemplateField, targetState: StateKey): boolean {
  if (!field.requiredForState) {
    return false;
  }

  const requiredOrder = stateOrder[field.requiredForState];
  const targetOrder = stateOrder[targetState];
  return requiredOrder !== undefined && targetOrder !== undefined && requiredOrder <= targetOrder;
}

function validateFieldConstraints(field: TemplateField, fv: PermitFieldValueInput | undefined): string | undefined {
  if (!fv) {
    return undefined;
  }

  switch (field.fieldType) {
    case TemplateFieldType.Text: {
      const textField = field as TextTemplateField;
      if (textField.maxLength && fv.stringValue && fv.stringValue.length > textField.maxLength) {
        return `Please enter at most ${textField.maxLength} characters for ${field.label.toLowerCase()}`;
      }
      break;
    }
    case TemplateFieldType.Number: {
      const numberField = field as NumberTemplateField;
      const numValue = fv.numberValue != null ? Number(fv.numberValue) : null;
      if (numValue === null || isNaN(numValue)) {
        break;
      }

      if (numberField.minValue != null && numValue < Number(numberField.minValue)) {
        return `Please enter a value of at least ${numberField.minValue} for ${field.label.toLowerCase()}`;
      }

      if (numberField.maxValue != null && numValue > Number(numberField.maxValue)) {
        return `Please enter a value of at most ${numberField.maxValue} for ${field.label.toLowerCase()}`;
      }

      if (numberField.decimalPlaces != null) {
        const parts = numValue.toString().split(".");
        const actualDecimals = parts.length > 1 ? parts[1].replace(/0+$/, "").length : 0;
        if (actualDecimals > numberField.decimalPlaces) {
          return `Please enter at most ${numberField.decimalPlaces} decimal places for ${field.label.toLowerCase()}`;
        }
      }
      break;
    }
  }

  return undefined;
}

/** Validates custom fields for mandatory requirements and field constraints when transitioning to a target state */
export function validateCustomFields(
  templateFields: TemplateField[],
  fieldValues: PermitFieldValueInput[],
  targetState: StateKey,
): FormError {
  const errors: FormError = {};

  for (const field of templateFields) {
    const fv = fieldValues.find((v) => v.fieldKey === field.key);

    // Check mandatory requirement (cumulative across states)
    if (isRequiredAtState(field, targetState) && isFieldEmpty(field, fv)) {
      errors[field.key] = `Please enter ${field.label.toLowerCase()}`;
      continue;
    }

    // Check field-type-specific constraints
    const constraintError = validateFieldConstraints(field, fv);
    if (constraintError) {
      errors[field.key] = constraintError;
    }
  }

  return errors;
}
